import {computed, inject, Injectable, signal} from '@angular/core';
import {HttpContext} from '@angular/common/http';
import {firstValueFrom} from 'rxjs';
import {
  AcceptTemplateScoreSuggestionRequestParams,
  DeleteTemplateScoreClarificationRequestParams,
  GetTemplateScoreClarificationsRequestParams,
  GetTemplateScoreSuggestionsRequestParams,
  RejectTemplateScoreSuggestionRequestParams,
  SaveTemplateScoresRequestParams,
  SuggestionConfidence,
  SuggestionStatus,
  TemplateScoreClarificationControllerService,
  TemplateScoreClarificationView,
  TemplateScoreControllerService,
  TemplateScoreInput,
  TemplateScoreSuggestionControllerService,
  TemplateScoreSuggestionResponse,
  TenderPhaseType,
  UpsertTemplateScoreClarificationRequestParams,
} from '@frontend/shared-api';
import {SKIP_ERROR_TOAST} from '@frontend/shared';
import {SubcriteriaAttribute} from '../provider-questionnaire.types';
import {
  BulkAcceptResult,
  OverrideValue,
  PersistedAttributeValue,
  SuggestionContext,
  SuggestionListFilter,
} from './template-suggestion.types';

@Injectable()
export class TemplateSuggestionStore {
  private readonly suggestionService = inject(TemplateScoreSuggestionControllerService);
  private readonly scoreService = inject(TemplateScoreControllerService);
  private readonly clarificationService = inject(TemplateScoreClarificationControllerService);

  private readonly contextState = signal<SuggestionContext | null>(null);
  private readonly suggestions = signal<TemplateScoreSuggestionResponse[]>([]);
  private readonly clarifications = signal<TemplateScoreClarificationView[]>([]);
  private readonly busySuggestionIds = signal<ReadonlySet<number>>(new Set());
  private readonly busyClarificationAttributeIds = signal<ReadonlySet<string>>(new Set());

  readonly filter = signal(SuggestionListFilter.ALL);
  readonly lastPersistedValue = signal<PersistedAttributeValue | null>(null);

  readonly context = this.contextState.asReadonly();
  readonly enabled = computed(() => this.contextState() !== null);
  readonly openSuggestions = computed(() =>
    this.suggestions().filter((suggestion) => suggestion.status === SuggestionStatus.OPEN),
  );
  private readonly openByAttribute = computed(() => this.firstPerAttribute(this.openSuggestions()));
  private readonly reviewRequiredByAttribute = computed(() =>
    this.firstPerAttribute(
      this.suggestions().filter(
        (suggestion) =>
          suggestion.status === SuggestionStatus.ACCEPTED && suggestion.reviewRequired,
      ),
    ),
  );
  private readonly clarificationByAttribute = computed(
    () =>
      new Map(
        this.clarifications().map((clarification) => [
          clarification.attributeDirectusId,
          clarification,
        ]),
      ),
  );
  readonly filterActive = computed(() => this.filter() !== SuggestionListFilter.ALL);

  async load(toolId: number, phase: TenderPhaseType): Promise<void> {
    this.contextState.set({toolId, phase});
    this.lastPersistedValue.set(null);
    const suggestionRequest: GetTemplateScoreSuggestionsRequestParams = {toolId, phase};
    const clarificationRequest: GetTemplateScoreClarificationsRequestParams = {toolId, phase};
    const [suggestions, clarifications] = await Promise.all([
      firstValueFrom(this.suggestionService.getTemplateScoreSuggestions(suggestionRequest)),
      firstValueFrom(
        this.clarificationService.getTemplateScoreClarifications(clarificationRequest),
      ),
    ]);
    this.suggestions.set(suggestions.suggestions);
    this.clarifications.set(clarifications.clarifications);
  }

  clear(): void {
    this.contextState.set(null);
    this.lastPersistedValue.set(null);
    this.suggestions.set([]);
    this.clarifications.set([]);
  }

  openSuggestionFor(directusId: string): TemplateScoreSuggestionResponse | undefined {
    return this.openByAttribute().get(directusId);
  }

  reviewRequiredSuggestionFor(directusId: string): TemplateScoreSuggestionResponse | undefined {
    return this.reviewRequiredByAttribute().get(directusId);
  }

  clarificationFor(directusId: string): TemplateScoreClarificationView | undefined {
    return this.clarificationByAttribute().get(directusId);
  }

  isBusy(suggestionId: number): boolean {
    return this.busySuggestionIds().has(suggestionId);
  }

  isClarificationBusy(directusId: string): boolean {
    return this.busyClarificationAttributeIds().has(directusId);
  }

  matchesFilter(attribute: SubcriteriaAttribute): boolean {
    switch (this.filter()) {
      case SuggestionListFilter.OPEN_SUGGESTION:
        return this.openByAttribute().has(attribute.directusId);
      case SuggestionListFilter.CLARIFICATION:
        return this.clarificationByAttribute().has(attribute.directusId);
      case SuggestionListFilter.UNANSWERED:
        return attribute.score === undefined || attribute.score === null;
      case SuggestionListFilter.REVIEW_REQUIRED:
        return this.reviewRequiredByAttribute().has(attribute.directusId);
      default:
        return true;
    }
  }

  highConfidenceOpenSuggestionsAmong(
    attributeDirectusIds: ReadonlySet<string>,
  ): TemplateScoreSuggestionResponse[] {
    return this.openSuggestions().filter(
      (suggestion) =>
        suggestion.confidence === SuggestionConfidence.HIGH &&
        attributeDirectusIds.has(suggestion.attributeDirectusId),
    );
  }

  async accept(suggestion: TemplateScoreSuggestionResponse): Promise<void> {
    await this.runBusy(suggestion.id, async () => {
      const request: AcceptTemplateScoreSuggestionRequestParams = {suggestionId: suggestion.id};
      const accepted = await firstValueFrom(
        this.suggestionService.acceptTemplateScoreSuggestion(request),
      );
      this.replace(accepted);
      this.lastPersistedValue.set(this.toPersistedValue(accepted));
    });
  }

  async reject(suggestion: TemplateScoreSuggestionResponse): Promise<void> {
    await this.runBusy(suggestion.id, async () => {
      const request: RejectTemplateScoreSuggestionRequestParams = {suggestionId: suggestion.id};
      const rejected = await firstValueFrom(
        this.suggestionService.rejectTemplateScoreSuggestion(request),
      );
      this.replace(rejected);
    });
  }

  async override(suggestion: TemplateScoreSuggestionResponse, value: OverrideValue): Promise<void> {
    const context = this.contextState();
    if (!context) return;

    await this.runBusy(suggestion.id, async () => {
      const templateScore: TemplateScoreInput = {
        toolId: context.toolId,
        attributeDirectusId: suggestion.attributeDirectusId,
        score: value.score,
        scoreAbsent: false,
        comment: value.comment,
        capabilityLevel: value.capabilityLevel,
        provisioningType: value.provisioningType,
        changeReason: value.reason,
      };
      const request: SaveTemplateScoresRequestParams = {
        phase: context.phase,
        templateScoresRequest: {templateScores: [templateScore]},
      };
      await firstValueFrom(this.scoreService.saveTemplateScores(request));
      this.replace({...suggestion, status: SuggestionStatus.SUPERSEDED});
      this.lastPersistedValue.set({
        directusId: suggestion.attributeDirectusId,
        score: value.score,
        capabilityLevel: value.capabilityLevel,
        provisioningType: value.provisioningType,
      });
    });
  }

  async acceptAll(suggestions: TemplateScoreSuggestionResponse[]): Promise<BulkAcceptResult> {
    const result: BulkAcceptResult = {accepted: 0, skipped: 0};
    const options = {context: new HttpContext().set(SKIP_ERROR_TOAST, true)};
    for (const suggestion of suggestions) {
      const request: AcceptTemplateScoreSuggestionRequestParams = {suggestionId: suggestion.id};
      try {
        const accepted = await firstValueFrom(
          this.suggestionService.acceptTemplateScoreSuggestion(request, 'body', false, options),
        );
        this.replace(accepted);
        this.lastPersistedValue.set(this.toPersistedValue(accepted));
        result.accepted += 1;
      } catch {
        result.skipped += 1;
      }
    }
    return result;
  }

  async markClarification(attributeDirectusId: string, note: string | null): Promise<void> {
    const context = this.contextState();
    if (!context) return;

    await this.runClarificationBusy(attributeDirectusId, async () => {
      const request: UpsertTemplateScoreClarificationRequestParams = {
        templateScoreClarificationRequest: {
          toolId: context.toolId,
          attributeDirectusId,
          phase: context.phase,
          note: note ?? undefined,
        },
      };
      const marked = await firstValueFrom(
        this.clarificationService.upsertTemplateScoreClarification(request),
      );
      this.clarifications.update((all) => [
        marked,
        ...all.filter((clarification) => clarification.attributeDirectusId !== attributeDirectusId),
      ]);
    });
  }

  async clearClarification(attributeDirectusId: string): Promise<void> {
    const context = this.contextState();
    if (!context) return;

    await this.runClarificationBusy(attributeDirectusId, async () => {
      const request: DeleteTemplateScoreClarificationRequestParams = {
        toolId: context.toolId,
        attributeDirectusId,
        phase: context.phase,
      };
      await firstValueFrom(this.clarificationService.deleteTemplateScoreClarification(request));
      this.clarifications.update((all) =>
        all.filter((clarification) => clarification.attributeDirectusId !== attributeDirectusId),
      );
    });
  }

  private firstPerAttribute(
    suggestions: TemplateScoreSuggestionResponse[],
  ): Map<string, TemplateScoreSuggestionResponse> {
    const byAttribute = new Map<string, TemplateScoreSuggestionResponse>();
    suggestions.forEach((suggestion) => {
      if (!byAttribute.has(suggestion.attributeDirectusId)) {
        byAttribute.set(suggestion.attributeDirectusId, suggestion);
      }
    });
    return byAttribute;
  }

  private async runBusy(suggestionId: number, work: () => Promise<void>): Promise<void> {
    this.busySuggestionIds.update((ids) => new Set([...ids, suggestionId]));
    try {
      await work();
    } finally {
      this.busySuggestionIds.update((ids) => {
        const remaining = new Set(ids);
        remaining.delete(suggestionId);
        return remaining;
      });
    }
  }

  private async runClarificationBusy(
    attributeDirectusId: string,
    work: () => Promise<void>,
  ): Promise<void> {
    this.busyClarificationAttributeIds.update((ids) => new Set([...ids, attributeDirectusId]));
    try {
      await work();
    } finally {
      this.busyClarificationAttributeIds.update((ids) => {
        const remaining = new Set(ids);
        remaining.delete(attributeDirectusId);
        return remaining;
      });
    }
  }

  private replace(updated: TemplateScoreSuggestionResponse): void {
    this.suggestions.update((all) =>
      all.map((suggestion) => (suggestion.id === updated.id ? updated : suggestion)),
    );
  }

  private toPersistedValue(suggestion: TemplateScoreSuggestionResponse): PersistedAttributeValue {
    return {
      directusId: suggestion.attributeDirectusId,
      score: suggestion.suggestedScore,
      capabilityLevel: suggestion.capabilityLevel,
      provisioningType: suggestion.provisioningType,
    };
  }
}
